/**
 * PATH: packages/email-hub/src/feature/components/smart-action-bar/ActionButton.tsx
 * FILE_ID: EMAILHUB-SMARTACTIONBAR-ACTIONBUTTON-001
 * PURPOSE: Per-kind presentation (label / icon / tone) for every disposition
 *          verb the SmartActionBar frame renders as a plain pill button.
 * ROLE IN ARCHITECTURE: Presentational leaf below the SmartActionBar frame.
 * MAIN EXPORTS: ActionButton, ACTION_META.
 * NON-RESPONSIBILITIES: No mutation calls — `onClick` is supplied by the
 *   frame, wired to useSmartActionBarRuntime's `run()`.
 * NOTES FOR FUTURE AI: `mark_done`'s tone is the one STATE-driven exception
 *   (grey while not done, green once accepted — operator 2026-06-17); every
 *   other verb keeps its fixed `primaryTone`. Extend ACTION_META, never a
 *   second per-kind switch, when adding a verb.
 */
import { Ban, Briefcase, CheckCircle2, Kanban, Loader2, Receipt, Trash2 } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { HeaderActionKind } from '../../lib/headerActions'

interface ActionMeta {
  label: string
  Icon: React.ComponentType<{ className?: string }>
  /** Tailwind classes for the FILLED (primary) treatment. */
  primaryTone: string
  /** Keyboard shortcut hint surfaced in the button tooltip (reader-scoped). */
  hint?: string
}

export const ACTION_META: Record<Exclude<HeaderActionKind, 'allocate_estimator' | 'mark_junk'>, ActionMeta> = {
  send_to_bills: {
    label: 'Send to Bills',
    Icon: Receipt,
    primaryTone: 'bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600',
  },
  open_card: {
    label: 'Card',
    Icon: Kanban,
    primaryTone: 'bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600',
  },
  open_job: {
    label: 'Open job',
    Icon: Briefcase,
    primaryTone: 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600',
  },
  move_to_trash: {
    // Label says "bin" to match BOTH this action's own toast ("Moved to
    // bin") AND the destination folder the operator actually sees — the
    // nav + page label it "Binned" (TrashPage), not "Trash". The kind
    // keeps its historical `move_to_trash` name (route is /email/trash);
    // only the user-facing copy is aligned (audit 2026-06-14).
    label: 'Move to bin',
    Icon: Trash2,
    primaryTone: 'bg-rose-600 text-white hover:bg-rose-700 border-rose-600',
  },
  auto_junk_rule: {
    label: 'Auto-trash rule',
    Icon: Ban,
    primaryTone: 'bg-rose-600 text-white hover:bg-rose-700 border-rose-600',
  },
  mark_done: {
    label: 'Done',
    Icon: CheckCircle2,
    primaryTone: 'bg-emerald-600 text-white hover:bg-emerald-700 border-emerald-600',
    hint: 'D',
  },
}

export function ActionButton({
  kind,
  variant,
  busy,
  disabled,
  done = false,
  onClick,
}: {
  kind: Exclude<HeaderActionKind, 'allocate_estimator' | 'mark_junk'>
  variant: 'primary' | 'secondary'
  busy: boolean
  disabled: boolean
  /** Only meaningful for `mark_done`: drives its grey→green state. */
  done?: boolean
  onClick: () => void
}) {
  const meta = ACTION_META[kind]
  const { Icon } = meta
  // `mark_done` is the one verb whose colour reflects STATE, not just
  // prominence: neutral/grey while the email is NOT done, then green to
  // signal acceptance once it is. Every other verb keeps its fixed tone.
  const primaryTone =
    kind === 'mark_done' && !done
      ? 'bg-muted text-foreground hover:bg-accent border-border'
      : meta.primaryTone
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      title={meta.hint ? `${meta.label} (${meta.hint})` : meta.label}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium',
        'transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        variant === 'primary'
          ? primaryTone
          : 'border-border bg-background text-foreground/80 hover:bg-accent',
        (busy || disabled) && 'opacity-60 cursor-not-allowed',
      )}
      data-testid={`smart-action-${kind}`}
    >
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin shrink-0" aria-hidden />
      ) : (
        <Icon className="h-3 w-3 shrink-0" aria-hidden />
      )}
      <span>{meta.label}</span>
    </button>
  )
}
